import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import AdminLayout from "../../platform/AdminLayout";
import { api } from "../../lib/api";
import PagePreview from "../../components/PagePreview";
import ButtonsEditor from "../../components/ButtonsEditor";
import { PeriodPicker, Bars, StatCard, dayPlus } from "../../components/stats";

const inputCls =
  "w-full rounded-xl border border-nuit-900/15 bg-white px-3.5 py-2.5 text-sm text-nuit-900 placeholder:text-steel/60 focus:border-electric focus:outline-none";

export default function Espace() {
  const [client, setClient] = useState(null);
  const [description, setDescription] = useState("");
  const [buttons, setButtons] = useState([]);
  const [from, setFrom] = useState(dayPlus(-6));
  const [to, setTo] = useState(dayPlus(0));
  const [stats, setStats] = useState(null);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [pwd, setPwd] = useState({ current: "", new: "", confirm: "" });
  const [pwdMsg, setPwdMsg] = useState("");

  useEffect(() => {
    api("/espace/me").then((c) => {
      setClient(c);
      setDescription(c.description || "");
      setButtons(c.buttons || []);
    }).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    api(`/espace/me/stats?date_from=${from}&date_to=${to}`).then(setStats).catch(() => {});
  }, [from, to]);

  if (!client) {
    return (
      <AdminLayout role="merchant">
        <p className="text-sm text-steel">{error || "Chargement de votre espace…"}</p>
      </AdminLayout>
    );
  }

  const ed = client.editable || {};

  const save = async () => {
    setError("");
    setSaved("");
    const body = {};
    if (ed.description) body.description = description;
    if (ed.links || ed.buttons) body.buttons = buttons;
    try {
      await api("/espace/me", { method: "PATCH", body });
      setSaved("Modifications enregistrées et publiées sur votre page.");
      setTimeout(() => setSaved(""), 5000);
    } catch (e) {
      setError(e.message); // les saisies sont conservées
    }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setPwdMsg("");
    if (pwd.new.length < 8) return setPwdMsg("Le nouveau mot de passe doit contenir 8 caractères minimum.");
    if (pwd.new !== pwd.confirm) return setPwdMsg("Les deux mots de passe ne correspondent pas.");
    try {
      await api("/auth/change-password", { method: "POST", body: { current: pwd.current, new: pwd.new } });
      setPwd({ current: "", new: "", confirm: "" });
      setPwdMsg("Mot de passe modifié avec succès.");
    } catch (err) {
      setPwdMsg(err.message);
    }
  };

  return (
    <AdminLayout role="merchant">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-nuit-900 sm:text-3xl" data-testid="espace-title">
            {client.business_name}
          </h1>
          <p className="mt-1 text-sm text-steel">Votre espace commerçant Noveocard.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-full border border-nuit-900/15 bg-white py-1.5 pl-4 pr-1.5">
            <span className="max-w-52 truncate font-mono text-xs text-nuit-900">{client.public_url}</span>
            <button onClick={() => { navigator.clipboard?.writeText(client.public_url); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              data-testid="espace-copy-url"
              className="flex items-center gap-1 rounded-full bg-nuit-900 px-3 py-1.5 text-[11px] font-semibold text-white">
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copié" : "Copier"}
            </button>
          </div>
          <a href={`/p/${client.slug}`} target="_blank" rel="noopener noreferrer" data-testid="espace-open-page"
            className="flex items-center gap-1.5 rounded-full bg-electric px-4 py-2.5 font-display text-xs font-semibold text-white hover:bg-electric-600">
            <ExternalLink className="h-3.5 w-3.5" /> Voir ma page
          </a>
        </div>
      </div>

      {(saved || error) && (
        <p className={`mt-5 rounded-xl p-3.5 text-sm font-medium ${saved ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}
          role={error ? "alert" : "status"} data-testid="espace-feedback">
          {saved || error}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          {(ed.description || ed.links || ed.buttons) && (
            <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
              <h2 className="font-display text-base font-bold text-nuit-900">Ma page digitale</h2>
              {ed.description && (
                <div className="mt-4">
                  <label className="mb-1 block text-xs font-semibold text-nuit-900">Description</label>
                  <textarea rows={2} maxLength={500} className={`${inputCls} resize-y`} value={description}
                    onChange={(e) => setDescription(e.target.value)} data-testid="espace-description" />
                </div>
              )}
              {(ed.links || ed.buttons) && (
                <div className="mt-5">
                  <ButtonsEditor buttons={buttons} onChange={setButtons}
                    canEditLinks={!!ed.links} canEditButtons={!!ed.buttons} canEditIcons={false} />
                </div>
              )}
              <button onClick={save} data-testid="espace-save"
                className="mt-6 rounded-full bg-electric px-6 py-3 font-display text-sm font-semibold text-white hover:bg-electric-600">
                Enregistrer et publier
              </button>
            </section>
          )}

          <section className="rounded-3xl border border-nuit-900/10 bg-white p-6" data-testid="espace-stats">
            <h2 className="font-display text-base font-bold text-nuit-900">Mes statistiques</h2>
            <p className="mt-1 text-xs text-steel">
              Consultation = ouverture de votre page. Clic = clic sur un bouton (un clic Google Avis ne
              garantit pas un avis publié).
            </p>
            <div className="mt-4">
              <PeriodPicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
            </div>
            {stats && (
              <>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <StatCard label="Consultations" value={stats.views} />
                  <StatCard label="Clics" value={stats.clicks} />
                </div>
                <div className="mt-6"><Bars series={stats.series} /></div>
                <ul className="mt-6 space-y-2">
                  {stats.per_button.map((b) => (
                    <li key={b.button} className="flex items-center justify-between rounded-xl bg-brume px-4 py-2.5 text-sm">
                      <span className="font-medium text-nuit-900">{b.label}</span>
                      <span className="font-display font-bold text-electric">{b.clicks}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="rounded-3xl border border-nuit-900/10 bg-white p-6">
            <h2 className="font-display text-base font-bold text-nuit-900">Mon mot de passe</h2>
            <form onSubmit={changePwd} className="mt-4 grid max-w-md gap-3.5">
              <input type="password" required className={inputCls} placeholder="Mot de passe actuel"
                value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                data-testid="pwd-current" aria-label="Mot de passe actuel" />
              <input type="password" required minLength={8} className={inputCls} placeholder="Nouveau mot de passe (8 caractères min.)"
                value={pwd.new} onChange={(e) => setPwd((p) => ({ ...p, new: e.target.value }))}
                data-testid="pwd-new" aria-label="Nouveau mot de passe" />
              <input type="password" required className={inputCls} placeholder="Confirmation"
                value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                data-testid="pwd-confirm" aria-label="Confirmation" />
              {pwdMsg && (
                <p className={`text-sm font-medium ${pwdMsg.includes("succès") ? "text-emerald-700" : "text-red-700"}`} role="status" data-testid="pwd-feedback">
                  {pwdMsg}
                </p>
              )}
              <button type="submit" data-testid="pwd-save"
                className="rounded-full bg-nuit-900 px-6 py-3 font-display text-sm font-semibold text-white hover:bg-nuit-800">
                Modifier mon mot de passe
              </button>
            </form>
          </section>
        </div>

        <div>
          <PagePreview page={{ ...client, description, buttons }} />
        </div>
      </div>
    </AdminLayout>
  );
}
